// ============================================================================
// Core Types & Enums
// ============================================================================

export type ContentTypeFilter = 'BOM_ONLY' | 'ILLUSTRATIONS_ONLY' | 'BOTH' | 'EMPTY';

export enum PdfImportErrorCode {
  PAGE_LIMIT_EXCEEDED = 'PAGE_LIMIT_EXCEEDED',
  FILE_SIZE_LIMIT_EXCEEDED = 'FILE_SIZE_LIMIT_EXCEEDED',
  MONTHLY_PAGE_LIMIT_EXCEEDED = 'MONTHLY_PAGE_LIMIT_EXCEEDED'
}

export type ExtractionJobPhase = 'VALIDATION' | 'EXTRACTION' | 'PROCESSING' | 'PUBLISHING';

export enum MatchingPageOutcomeType {
  NEW_PARTS_PAGE = 'NEW_PARTS_PAGE',
  NEW_PAGE_SHARED_SVG = 'NEW_PAGE_SHARED_SVG',
  OVERWRITE_EXISTING = 'OVERWRITE_EXISTING'
}

export type MatchStatus = 'EXISTING' | 'NEW';

export enum PageOrigin {
  EXTRACTED_FROM_ONE_PDF_PAGE = 'EXTRACTED_FROM_ONE_PDF_PAGE',
  COMPOSED_OF_MULTIPLE_PDF_PAGES = 'COMPOSED_OF_MULTIPLE_PDF_PAGES'
}

export type PdfImportSortField = 'name' | 'sourceFilename' | 'created' | 'updated' | 'status';

export type PdfImportSortOrder = 'ASC' | 'DESC';

export type PdfImportStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'PUBLISHING'
  | 'PUBLISHED'
  | 'RESETTING'
  | 'FAILED'
  | 'DECLINED';

export type ReviewStatus = 'APPROVED' | 'PENDING' | 'DROPPED' | 'NO_STATUS';

// ============================================================================
// Common/Shared Interfaces
// ============================================================================

export interface Bounds {
  left: number
  top: number
  height: number
  width: number
}

export interface FieldError {
  code?: string;
  field: string;
  message: string;
}

export interface ValidationErrorResponse {
  errors?: FieldError[];
  message: string;
}

// ============================================================================
// Import Management
// ============================================================================

export interface ColumnMapping {
  defaultPartUnitOfMeasure?: string;
  item: string;
  pagePartTag?: PagePartTagMapping | null;
  partNumber: string;
  partTranslationName: string;
  partUnitOfMeasure?: string;
  quantity: string;
}

export interface PagePartTagMapping {
  columnName: string;
  tagName: string;
}

// Settings remembered by the "Remember import settings" checkbox in PdfImportCreateModal.vue.
export interface RememberedImportSettings {
  defaultPartNumberCode?: string | null;
  /** Raw comma-separated text entered in the mapped Part Number values field */
  defaultPartNumberCodeAliases?: string | null;
  defaultSupplierId?: number | null;
  columnMapping: Omit<ColumnMapping, 'pagePartTag'>;
  pagePartTag?: PagePartTagMapping | null;
}

export interface PdfImportAdminResponseDetails extends PdfImportAdminResponseRow {
  tenantId: number;
  userId: number;
  storageUri: string;
  columnMapping?: ColumnMapping;
  failureDetails?: string;
  fileSizeBytes?: number;
  pageCount?: number;
  providerJobId?: string;
}

export interface PdfImportAdminResponseRow {
  id: number;
  name: string;
  sourceFilename: string;
  status: PdfImportStatus;
  created: string;
  updated: string;
  failureCode?: string;
  failureMessage?: string;
}

export interface PdfImportCreateRequest {
  importName: string;
  defaultSupplierId: number;
  defaultTranslationLocale: string;
  columnMapping: ColumnMapping;
  defaultPartNumberCode?: string;
  /** Source Part Number values that resolve to defaultPartNumberCode (ticket #5054). */
  defaultPartNumberCodeAliases?: string[];
}

export interface PdfImportCreateResponse {
  id: number;
  message: string;
  errors?: FieldError[];
}

export interface PdfImportErrorResponse {
  id?: number; // Job ID - present for all 400 errors (FAILED status), absent for 402 (DECLINED status)
  error: PdfImportErrorCode;
  message: string;
  errors?: FieldError[]; // Field-level validation errors
  pageCount?: number; // Only for PAGE_LIMIT_EXCEEDED
  maxPages?: number; // Only for PAGE_LIMIT_EXCEEDED
  fileSizeBytes?: number; // Only for FILE_SIZE_LIMIT_EXCEEDED
  maxSizeBytes?: number; // Only for FILE_SIZE_LIMIT_EXCEEDED
}

export interface PdfImportSearchItem {
  id: number;
  name: string;
  sourceFilename: string;
  status: PdfImportStatus;
  locked: boolean;
  created: string;
  updated: string;
  failureMessage?: string;
}

export interface PdfImportSearchParams {
  createdAfter?: string;
  createdBefore?: string;
  limit?: number;
  offset?: number;
  order?: PdfImportSortOrder;
  q?: string;
  sort?: PdfImportSortField;
  status?: PdfImportStatus;
  updatedAfter?: string;
  updatedBefore?: string;
}

export interface PdfImportSearchResult {
  totalCount: number;
  limit: number;
  offset: number;
  items: PdfImportSearchItem[];
  matchCount?: number | null;
}

export interface PdfImportSubmission {
  file: File;
  request: PdfImportCreateRequest;
}

// ============================================================================
// Job Errors
// ============================================================================

export interface JobErrorsParams {
  jobVersion?: number;
  phase?: ExtractionJobPhase;
}

export interface JobErrorsResponse {
  errors: FieldError[];
}

// ============================================================================
// Overview & Pages
// ============================================================================

export interface BulkUpdateReviewStatusResponse {
  reviewProgress: ReviewStatusCounts;
  updatedCount: number;
}

export interface PageIdsResponse {
  count: number;
  pageIds: number[];
}

export interface PdfImportOverviewParams {
  limit?: number;
  offset?: number;
  q?: string;
  /** Multi-value. Omit when all or none selected. */
  contentType?: ContentTypeFilter[];
  /** Multi-value. Omit when all 4 or none selected. Default: APPROVED + PENDING + NO_STATUS. */
  reviewStatus?: ReviewStatus[];
  pageNumberFilter?: string;
  incompleteBomOnly?: boolean;
}

export interface PdfImportOverviewResponse {
  importId: number;
  importName: string;
  sourceFilename: string;
  workflowStatus: PdfImportStatus;
  locked: boolean;
  defaultTranslationLocale: string;
  filteredPageCount: number;
  totalPages: number;
  pages: PdfImportPageTile[];
  publishProgress: PublishProgressResponse | null;
  reviewStatusCounts: ReviewStatusCounts;
}

export interface ReviewStatusCounts {
  approvedCount: number;
  pendingCount: number;
  droppedCount: number;
}

export interface PdfImportPageDto {
  pageId: number;
  importId: number;
  pageNumber: number;
  sourceFilename: string;
  pageTitle: string | null;
  bomItemCount: number;
  illustrationCount: number;
  reviewStatus: ReviewStatus | null;
  composed: boolean;
  created: string;
  updated: string;
}

export interface PdfImportPageTile {
  pageId: number;
  pageNumber: number;
  /** null = original candidate; 1/2/3... = a duplicated copy */
  copyNumber: number | null;
  thumbnailUrl: string;
  pageTitle: string | null;
  filename: string | null;
  bomItemCount: number;
  illustrationCount: number;
  composed: boolean;
  hasBomIssues: boolean;
  reviewStatus: ReviewStatus | null;
}

// ============================================================================
// Page Details
// ============================================================================

export interface MatchingPageResult {
  matchingPageId: number | null;
  matchingPageName: string | null;
  outcomeType: MatchingPageOutcomeType;
}

export interface PageDetailsInfoRequest {
  pageFilename: string | null;
  reviewStatus: ReviewStatus | null;
  translationDescription: string | null;
  translationName: string | null;
}

export interface PageDetailsInfoResponse {
  contentExtractionPageId: number;
  pageNumber: number;
  /** null = original candidate; 1/2/3... = a duplicated copy */
  copyNumber: number | null;
  /** "X" in "X / Total" — 1-indexed rank in the flat candidate list. Not stable across time. */
  positionInList: number;
  /** "Total" in "X / Total" — every candidate (original + every copy). Not stable across time. */
  totalCandidateCount: number;
  pageOrigin: PageOrigin;
  type: string;
  locked: boolean;
  thumbnailUrl: string;
  pageFilename: string | null;
  sourceFilename: string;
  bomItemCount: number;
  illustrationCount: number;
  reviewStatus: ReviewStatus | null;
  matchingPage: MatchingPageResult;
  translation: PageTranslation;
  navigation: PageNavigation;
}

/**
 * Page ID refers to CONTENT_EXTRACTION_PAGE record as we work within the context of a PDF import.
 * `Matching page` refers to the existing page in the database that matches the content of the page being imported.
 */
export interface PageIdLookupResponse {
  pageId: number;
  pageNumber: number;
  /** null = original candidate; 1/2/3... = a duplicated copy */
  copyNumber: number | null;
  /** Echoes back the requested position — a lookup either resolves exactly or 404s, never clamps. */
  positionInList: number;
}

export interface NavigationPage {
  pageId: number;
  pageNumber: number;
  /** null = original candidate; 1/2/3... = a duplicated copy */
  copyNumber: number | null;
}

export interface PageNavigation {
  first: NavigationPage | null;
  last: NavigationPage | null;
  next: NavigationPage | null;
  previous: NavigationPage | null;
}

export interface PageTranslation {
  description: string | null;
  locale: string;
  name: string | null;
}

// ============================================================================
// BOM (Bill of Materials)
// ============================================================================

export interface PagePartTag {
  id: number | null;
  name: string | null;
  value: string | null;
}

export interface PartNumberCode {
  id: number;
  code: string;
  description: string | null;
}

export interface BomItemDto {
  id: number | null;
  displayOrder: number;
  item: string | null;
  matchStatus: MatchStatus;
  pagePartTag: PagePartTag | null;
  partNameLocked: boolean;
  partName: string | null;
  partNumber: string | null;
  partNumberCode: PartNumberCode | null;
  quantity: string | null;
  supplierId: number;
  uom: string | null;
  uomLocked: boolean;
  /** The obsolete Part Number that was superseded to resolve this row, if any. Non-null means this EXISTING row was
   * resolved via part supersession rather than a direct Part Number + Supplier match. */
  supersededPartNumber: string | null;
  /** The Supplier of that superseded Part, when recorded. Independent of supplierId (the replacement's). Omitted when null. */
  supersededPartSupplierId?: number | null;
}

export interface BomRowState extends BomItemDto {
  _clientKey: string;
  _lookupPending: boolean;
  _lookupToken: number;
  _resolvedToken: number;
}

export interface BomSaveItemDto {
  displayOrder: number;
  item: string | null;
  pagePartTagId: number | null;
  pagePartTagValue: string | null;
  partName: string | null;
  partNumber: string | null;
  partNumberCodeId: number | null;
  quantity: string | null;
  supplierId: number;
  uom: string | null;
  /** Echoes BomItemDto.supersededPartNumber back. Defaults to null (not superseded) on the backend when omitted. */
  supersededPartNumber?: string | null;
  /** Echoes BomItemDto.supersededPartSupplierId back, so the superseded Part's identity stays with its row. */
  supersededPartSupplierId?: number | null;
}

export interface BomSaveRequest {
  items: BomSaveItemDto[];
}

export interface BomViewResponse {
  items: BomItemDto[];
  bomItemCount: number;
  defaultSupplierId: number;
  defaultUom: string | null;
  extractionPageId: number;
  locale: string;
  locked: boolean;
  pagePartTagName: string | null;
  showPagePartTagColumn: boolean;
  sourceFilename: string;
}

export interface PartLookupExisting {
  matchStatus: 'EXISTING';
  partCode: string | null;
  partCodeDescription: string | null;
  partName: string | null;
  partNumber: string;
  partNumberCodeId: number | null;
  uom: string | null;
}

export interface PartLookupNew {
  matchStatus: 'NEW';
  partCode: string | null;
  partCodeDescription: string | null;
  partNumber: string | null;
  partNumberCodeId: number | null;
}

export type PartLookupResponse = PartLookupNew | PartLookupExisting;

// ============================================================================
// Illustrations
// ============================================================================

export interface Illustration {
  /** null = unsaved row added manually via "Add from Page"; persisted rows always have an id */
  id: number | null
  sortOrder: number
  /** Use directly as <img src> — do not reconstruct from IDs */
  imageUrl: string
  bounds: Bounds
}

export interface IllustrationRowState extends Illustration {
  _boundsChanged: boolean
  _clientKey: string
  /** True for rows added manually via "Add from Page" that have not been saved yet */
  _isNew: boolean
  /** Blob URL of the unsaved bounds preview JPEG. Null until bounds are adjusted.
   *  Must be revoked via URL.revokeObjectURL() on save or revert. */
  _previewUrl: string | null
}

export interface IllustrationSaveItem {
  /** null = create a new illustration on save */
  id: number | null
  sortOrder: number
  bounds: Bounds
}

export interface IllustrationsSaveRequest {
  illustrations: IllustrationSaveItem[]
}

export interface IllustrationsViewResponse {
  pageId: number
  locked: boolean
  sourceFilename: string
  illustrations: Illustration[]
}

// ============================================================================
// Merge Operations
// ============================================================================

export interface AutoMergeResult {
  merges: MergeDetail[]
  totalMergesPerformed: number
  totalPagesDropped: number
}

export interface DroppedPageInfo {
  pageId: number
  pageNumber: number
}

export interface MergeDetail {
  targetPageId: number
  targetPageNumber: number
  pageTitle: string | null
  composed: boolean
  targetBomItemCount: number
  targetIllustrationCount: number
  bomItemsCopied: number
  illustrationsCopied: number
  droppedPages: DroppedPageInfo[]
}

export interface MergePageRequest {
  /** All selected page IDs. Order does not matter — backend targets lowest pageNumber. */
  pageIds: number[]
}

export interface MergePageResponse {
  targetPageId: number
  targetPageNumber: number
  targetBomItemCount: number
  targetIllustrationCount: number
  /** True if any non-empty content was copied; drives the composed indicator on the target tile. */
  composed: boolean
  droppedPageIds: number[]
  reviewProgress: ReviewStatusCounts
}

// ============================================================================
// Duplicate Operations (#5029)
// ============================================================================

export interface BulkDuplicatePagesRequest {
  /** CONTENT_EXTRACTION_PAGE.id values initiating duplication. Non-empty, no duplicates. */
  pageIds: number[]
}

export interface DuplicatePageResponse {
  /** New candidate's CONTENT_EXTRACTION_PAGE.id — navigate here (Page Details flow). */
  pageId: number
  pageNumber: number
  /** Always >= 1 for a duplicate result — never null. */
  copyNumber: number
  /** The pageId that initiated this duplication. */
  sourcePageId: number
  pageTitle: string | null
  filename: string | null
  bomItemCount: number
  illustrationCount: number
}

export interface BulkDuplicatePagesResponse {
  duplicates: DuplicatePageResponse[]
  successCount: number
  /** pageIds from the request that could not be duplicated. Still a 200 OK response. */
  failedPageIds: number[]
  reviewProgress: ReviewStatusCounts
}

// ============================================================================
// Publishing
// ============================================================================

export enum BookTargetMode {
  NEW = 'NEW'
}

export enum MatchingBookOutcomeType {
  AVAILABLE = 'AVAILABLE',
  IDENTIFIER_TAKEN = 'IDENTIFIER_TAKEN'
}

export interface BookTarget {
  mode: BookTargetMode
  /** Must be non-blank and unique within the tenant */
  identifier: string
  name: string
  description: string | null
}

export interface MatchingBookResult {
  matchingBookId: number | null
  outcomeType: MatchingBookOutcomeType
}

export interface PublishSelectedRequest {
  pageIds: number[]
  skipAutoHotpointing: boolean
  /** null → Pages Only flow; populated → Create New Book flow */
  bookTarget: BookTarget | null
}

export interface PublishSelectedResponse {
  publishJobId: number
}

export interface PublishProgressResponse {
  total: number
  pending: number
  publishing: number
  success: number
  failed: number
  percentComplete: number
}

export enum PublishJobStatus {
  PENDING = 'PENDING',
  PUBLISHING = 'PUBLISHING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}

export enum PublishPageStatus {
  PENDING = 'PENDING',
  PUBLISHING = 'PUBLISHING',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED'
}

// ─── Publishing History (#4775) ───────────────────────────────────────────────

export enum PublishingStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export enum PageResult {
  PUBLISHED_CREATED = 'PUBLISHED_CREATED',
  PUBLISHED_UPDATED = 'PUBLISHED_UPDATED',
  FAILED = 'FAILED',
  SKIPPED = 'SKIPPED',
}

export enum BookResult {
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  SKIPPED = 'SKIPPED',
}

export interface BulkResetPagesRequest {
  /** CONTENT_EXTRACTION_PAGE.id values to reset. 1–10 entries. */
  pageIds: number[]
}

export interface BulkResetPagesResponse {
  /** Number of pages that were successfully reset. */
  successCount: number
  /**
   * IDs of pages that could not be reset.
   * Most common cause: hires.png missing from EFS for an illustration page.
   * DB changes for these pages were rolled back.
   */
  failedPageIds: number[]
}

export enum PublishJobResultFilter {
  ALL = 'ALL',
  PUBLISHED = 'PUBLISHED',
  FAILED = 'FAILED',
}

export interface PublishJobListResponse {
  total: number
  jobs: PublishJobSummary[]
}

export interface PublishJobResultCounts {
  pagesPublished: number
  pagesFailed: number
  bookCreated: boolean
}

export interface PublishJobSummary {
  publishJobId: number
  jobNumber: number
  publishingStatus: PublishingStatus
  resultCounts: PublishJobResultCounts
  submittedAt: string
  completedAt: string | null
}

export interface PublishJobPageResultsResponse {
  importLocale: string
  pages: PublishJobPageResult[]
  filteredPageCount: number
  totalPageCount: number
}

export interface PublishJobPageResult {
  contentExtractionPageId: number
  pageNumber: number
  translationName: string
  pageFilename: string | null
  pageResult: PageResult
  publishedPageId: number | null
  errorMessage: string | null
}

export interface PublishJobBookResultResponse {
  hasBookOperation: boolean
  book: PublishJobBookResult | null
}

export interface PublishJobBookResult {
  bookIdentifier: string
  bookName: string
  bookLocale: string
  bookResult: BookResult
  resolvedMediaId: number | null
  errorMessage: string | null
}

// ============================================================================
// Reset Operations
// ============================================================================

export interface BulkResetPagesRequest {
  /** CONTENT_EXTRACTION_PAGE.id values to reset. 1 or more entries, no hard limit. */
  pageIds: number[]
}

export interface BulkResetPagesResponse {
  /** Number of pages that were successfully reset. */
  successCount: number
  /**
   * IDs of pages that could not be reset.
   * Most common cause: hires.png missing from EFS for an illustration page.
   * DB changes for these pages were rolled back.
   */
  failedPageIds: number[]
}

export interface ResetJobSubmittedResponse {
  /** The CONTENT_EXTRACTION_JOB.id submitted for async reset. Poll this job's derived status. */
  contentExtractionJobId: number
}
